/** The Activities grid search namespaces wired to their suggestion fetchers.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators` on
 * ActivityFilterNode (contract-tested). `exact` is deliberately omitted wherever
 * `iexact` alone serialises to `=` and re-parses to `iexact`
 * (type/price_type/payment_type/currency/tenant/country) — the backend still
 * accepts `exact`/id via its all-digits fallback. `category` keeps BOTH `exact`
 * and `iexact` so the widget offers the per-suggestion "by ID" (`=#`) commit
 * (operators.ts needs both). `id`/`price_level` are integer; `cost` mirrors the
 * api-requests `duration` (exact/gt/lt/range under one `=`, `>` via `100..`,
 * `<` via `..50`).
 *
 * Category suggestions are per-tenant: the page passes the picker's
 * `tour_operator_id` via extraFilterValues; no tenant ⇒ no category suggestions.
 */
import * as categoriesApi from '@/api/activityCategories';
import * as pickers from '@/api/pickers';
import type { NamespaceConfigMap } from '@/components/QuerySearch';

/** == ActivityFilterNode x-*-labels (contract-tested), in value order (index = value);
 *  `PAYMENT_TYPE_LABELS` also words the grid's Payment type column. */
export const TYPE_LABELS = ['Things to do', 'Food & drink', 'Lodging', 'Transportation', 'Attraction'];
export const PRICE_TYPE_LABELS = ['No price', 'Price level', 'Exact price', 'Price range'];
export const PAYMENT_TYPE_LABELS = ['Cost is included', 'Pay at location', 'Prepaid add-on'];

/** Price levels: show the $-glyph label, COMMIT the integer value (a `$` would
 *  400 against the backend `_require_int`). */
const PRICE_LEVELS = [
  { value: '1', label: '1 · $' },
  { value: '2', label: '2 · $$' },
  { value: '3', label: '3 · $$$' },
  { value: '4', label: '4 · $$$$' },
];

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

function priceLevelSuggestions(query: string) {
  const q = query.toLowerCase();
  return Promise.resolve(
    PRICE_LEVELS.filter((p) => p.value === q || p.label.toLowerCase().includes(q)).map((p) => ({
      value: p.value,
      label: p.label,
    })),
  );
}

/** Tenant-scoped category suggestions — reuses the toolbar CategoryFilter's
 *  endpoint. `extra.tour_operator_id` is injected by the page via
 *  QuerySearchInput's `extraFilterValues` when a tenant is picked. Each option
 *  carries `id` so the widget can offer the "by ID" (exact) commit. */
function fetchCategorySuggestions(query: string, extra: Record<string, string>, _signal: AbortSignal) {
  const topId = extra.tour_operator_id;
  if (!topId || !/^\d+$/.test(topId)) return Promise.resolve([]); // categories are meaningless without a tenant
  return categoriesApi
    .list({ tour_operator_id: Number(topId), search: query || undefined, pageSize: 20 })
    .then((r) => r.results.map((c) => ({ value: c.title, label: c.title, id: c.id })));
}

/** Country suggestions via the global `/countries/` picker (id-bearing). */
function fetchCountrySuggestions(query: string, _extra: Record<string, string>, _signal: AbortSignal) {
  if (!query.trim()) return Promise.resolve([]);
  return pickers
    .listCountries(query)
    .then((r) => r.results.map((c) => ({ value: c.name, label: c.name, id: c.id })));
}

export function buildActivityNamespaces(): NamespaceConfigMap {
  return {
    name: {
      label: 'name',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Activity name.',
      examples: ['name=~sunset', 'name="City Walking Tour"'],
    },
    display_name: {
      label: 'display_name',
      operators: ['icontains'],
      valueType: 'string',
      help: 'Client-facing display name.',
      examples: ['display_name=~kayak'],
    },
    type: {
      label: 'type',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(TYPE_LABELS),
      help: 'Content type: Things to do / Food & drink / Lodging / Transportation / Attraction (or 1-5).',
      examples: ['type=Attraction', 'type=3'],
    },
    category: {
      label: 'category',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: fetchCategorySuggestions,
      help: 'Activity category (title). Pick a suggestion to pin by id. Scoped to the picked tenant.',
      examples: ['category=~museum', 'category="Water sports"'],
    },
    country: {
      label: 'country',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: fetchCountrySuggestions,
      help: 'Country by name or 2-letter code; an all-digits value matches the country id.',
      examples: ['country=France', 'country=~portug'],
    },
    price_type: {
      label: 'price_type',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(PRICE_TYPE_LABELS),
      help: 'Pricing model: No price / Price level / Exact price / Price range (or 0-3).',
      examples: ['price_type="Price range"'],
    },
    payment_type: {
      label: 'payment_type',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(PAYMENT_TYPE_LABELS),
      help: 'How it is paid: Cost is included / Pay at location / Prepaid add-on (or 0-2).',
      examples: ['payment_type="Pay at location"'],
    },
    price_level: {
      label: 'price_level',
      operators: ['exact', 'range'],
      valueType: 'integer',
      fetchSuggestions: priceLevelSuggestions,
      help: 'Price level 1-4 ($ to $$$$). `=` a single level, or a range like 2..4.',
      examples: ['price_level=2', 'price_level=2..4'],
    },
    cost: {
      label: 'cost',
      operators: ['exact', 'gt', 'lt', 'range'],
      valueType: 'string',
      help: 'The FROM price (v2_cost_min). Open ranges give >/< : `100..` (over 100), `..50` (under 50), `100..500` (between). Pair with currency.',
      examples: ['cost=100..500', 'cost=50..', 'cost=..25'],
    },
    currency: {
      label: 'currency',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Cost currency, 3-letter code; an all-digits value matches the currency id.',
      examples: ['currency=USD', 'currency=EUR'],
    },
    custom_id: {
      label: 'custom_id',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'External / import id.',
      examples: ['custom_id=ACT-4471', 'custom_id=~ACT-'],
    },
    tenant: {
      label: 'tenant',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Owning tour operator by code; an all-digits value matches the tenant id. "system" = orphaned (no tenant).',
      examples: ['tenant=acme', 'tenant=system'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When created, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['created=2026-01-01..2026-07-01'],
    },
    updated: {
      label: 'updated',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When last edited, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['updated=2026-07-01..2026-08-01'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Activity id.',
      examples: ['id=88213'],
    },
  };
}
